import { type ShopifyProduct } from '@/hooks/queries/use-shopify-products-query';
import {
  MentionBoostTargetLocationType,
  UTM_KEY_REGEX,
  buildProductUrl,
  normalizeUrlBase,
  type MentionBoostSetupProductDto,
  type MentionBoostTargetLocationDto,
  type UtmParameter,
} from '@fluenceur/types';

// Product-page URL resolution is shared with the backend launch snapshot so the
// preview and the frozen URL never drift. Re-exported here to keep call sites in
// this feature importing from a single local module.
export { buildProductUrl, normalizeUrlBase };

// The four keys of the Fluenceur UTM convention. Their key is read-only in the
// editor (only the value is editable); any other pair is a user-added custom key.
export const UTM_CONVENTION_KEYS = new Set([
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
]);

export const isConventionUtmKey = (key: string): boolean =>
  UTM_CONVENTION_KEYS.has(key.trim().toLowerCase());

// Prepare the stored UTM list for the save payload. Drops pairs the backend
// would reject — an empty key, an invalid key format, or a duplicate key — and
// lowercases keys, so the auto-save payload is always valid (no 422 retry loop).
// Empty VALUES are kept (filtered only when applied at launch). null (pristine)
// and [] (disabled) pass through unchanged.
export const cleanUtmParameters = (value: UtmParameter[] | null): UtmParameter[] | null => {
  if (value === null) return null;
  const seen = new Set<string>();
  const result: UtmParameter[] = [];
  for (const pair of value) {
    const key = pair.key.trim().toLowerCase();
    if (key.length === 0 || !UTM_KEY_REGEX.test(key) || seen.has(key)) continue;
    seen.add(key);
    result.push({ key, value: pair.value });
  }
  return result;
};

// An editor row: a UTM pair plus a client-side identity that survives insertions
// and removals. React keys must not be the array index — removing a row in the
// middle would otherwise re-key every following row and steal focus mid-edit.
// The id never leaves the editor: `cleanUtmParameters` rebuilds `{key, value}`
// pairs for the save payload.
export interface UtmRow extends UtmParameter {
  id: string;
}

let utmRowSequence = 0;

// Attach a fresh identity to a pair. Called when a row enters the editor (the
// dynamic default convention, a hydrated server list, a brand-added row).
export const toUtmRow = (pair: UtmParameter): UtmRow => {
  utmRowSequence += 1;
  return { ...pair, id: `utm-${utmRowSequence}` };
};

export const toUtmRows = (pairs: UtmParameter[]): UtmRow[] => pairs.map(toUtmRow);

// Whether a row's key is invalid (bad format) or duplicates an earlier row.
export type UtmKeyError = 'invalid' | 'duplicate' | null;

export interface UtmRowMeta {
  // The key is read-only: a convention key at its canonical (first) position.
  // Derived from ORIGIN (first occurrence), not the live text, so typing a
  // convention key into a custom row never turns its input read-only.
  keyReadOnly: boolean;
  // Set for an editable (custom) row whose key is invalid or duplicated.
  keyError: UtmKeyError;
}

// Classify each UTM row for the editor: which keys are read-only (convention),
// and which editable keys are invalid or duplicated. Single source consumed by
// UtmControls so rendering and validation stay consistent.
export const describeUtmRows = (parameters: UtmParameter[]): UtmRowMeta[] => {
  const firstIndexByKey = new Map<string, number>();
  parameters.forEach((pair, index) => {
    const key = pair.key.trim().toLowerCase();
    if (key.length > 0 && !firstIndexByKey.has(key)) firstIndexByKey.set(key, index);
  });

  return parameters.map((pair, index) => {
    const key = pair.key.trim().toLowerCase();
    const isFirstOccurrence = firstIndexByKey.get(key) === index;

    if (isConventionUtmKey(key) && isFirstOccurrence) {
      return { keyReadOnly: true, keyError: null };
    }

    let keyError: UtmKeyError = null;
    if (key.length > 0) {
      if (!UTM_KEY_REGEX.test(key)) keyError = 'invalid';
      else if (!isFirstOccurrence) keyError = 'duplicate';
    }
    return { keyReadOnly: false, keyError };
  });
};

// Compact "host/path" label for the launch recap, e.g. naa.paris/products/serum.
// Falls back to the raw string when the URL cannot be parsed.
export const shortDestinationLabel = (url: string): string => {
  try {
    const parsed = new URL(url);
    const path = parsed.pathname === '/' ? '' : parsed.pathname;
    return `${parsed.host}${path}`;
  } catch {
    return url;
  }
};

export const isValidHttpUrl = (value: string): boolean => {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

export const productFromShopifyProduct = (
  product: ShopifyProduct
): MentionBoostSetupProductDto => ({
  shopifyProductId: product.id,
  title: product.title,
  imageUrl: product.featuredImageUrl ?? null,
  handle: product.handle,
});

// Intl.DisplayNames construction is the expensive part (.of() is cheap), so cache
// one instance per locale instead of rebuilding it for every mapped country code.
const regionNamesByLocale = new Map<string, Intl.DisplayNames | null>();

function regionDisplayNames(locale: string): Intl.DisplayNames | null {
  if (!regionNamesByLocale.has(locale)) {
    regionNamesByLocale.set(
      locale,
      typeof Intl.DisplayNames === 'function'
        ? new Intl.DisplayNames([locale], { type: 'region' })
        : null
    );
  }
  return regionNamesByLocale.get(locale) ?? null;
}

/** Build a target-location DTO from a 2-letter country code, naming it for the locale. */
export const buildTargetLocation = (
  countryCode: string,
  locale: string
): MentionBoostTargetLocationDto => {
  const upper = countryCode.toUpperCase();

  return {
    key: `country:${upper}`,
    name: regionDisplayNames(locale)?.of(upper) ?? upper,
    type: MentionBoostTargetLocationType.COUNTRY,
    countryCode: upper,
  };
};
